<template>
    <div>

        <!-- Шаг 1: Email -->
        <template v-if="! sentTo.length">

            <div class="login-form">
                <div class="login-field">
                    <input
                        v-model="email"
                        type="email"
                        placeholder="Email address"
                        aria-label="Email address"
                        autocomplete="email"
                        @keydown.enter="submitEmail"
                    >
                </div>
                <button
                    :class="`btn btn--primary-invert btn--lg btn--block login-btn ${ isLoading ? 'is-loading' : '' }`"
                    :disabled="isLoading"
                    type="button"
                    @click="submitEmail"
                >Send login link</button>
            </div>

            <p class="login-error" role="alert" v-if="error.length">{{ error }}</p>

            <div class="login-hint">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                </svg>
                We'll email you a secure sign-in link. New here? Your account is created automatically.
            </div>

        </template>

        <!-- Шаг 2: письмо ушло - код или магик из письма -->
        <template v-else>

            <div class="login-success">
                <div class="login-success-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                    </svg>
                </div>
                <h3>Check your email</h3>
                <p>We sent a login link and a 6-digit code to</p>
                <p class="email-highlight">{{ sentTo }}</p>

                <div class="login-code">
                    <div class="login-code-inputs">
                        <input
                            v-for="( digit, index ) in code"
                            :key="index"
                            :ref="el => { if( el ) codeInputs[ index ] = el }"
                            v-model="code[ index ]"
                            type="text"
                            inputmode="numeric"
                            maxlength="1"
                            class="login-code-cell"
                            :disabled="blocked"
                            @input="onCodeInput( index, $event )"
                            @keydown="onCodeKeydown( index, $event )"
                            @paste="onCodePaste( $event )"
                            @focus="$event.target.select()"
                        >
                    </div>
                    <button
                        :class="`btn btn--primary-invert btn--lg btn--block login-btn ${ isLoading ? 'is-loading' : '' }`"
                        :disabled="isLoading || ! isCodeComplete || blocked"
                        type="button"
                        @click="verifyCode"
                    >Sign in with code</button>
                    <p class="login-error" role="alert" v-if="error.length">{{ error }}</p>
                </div>

                <!-- 429: токен сожжён попытками - и код, и magic-ссылка письма мертвы -->
                <div class="login-resend" v-if="blocked">
                    Too many attempts. This code and the link from the email no longer work.
                    <template v-if="time > 0">New code available in <b>{{ formattedTime }}</b></template>
                    <a href="#" @click.prevent="resendCode" v-else>Request a new code</a>
                </div>
                <div class="login-resend" v-else-if="time > 0">
                    New code available in <b>{{ formattedTime }}</b>
                </div>
                <div class="login-resend" v-else>
                    Didn't receive the code?
                    <a href="#" @click.prevent="resendCode">Resend</a>
                </div>

                <a class="login-back" href="#" @click.prevent="backToForm">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round">
                        <path d="M19 12H5"/>
                        <path d="M12 19l-7-7 7-7"/>
                    </svg>
                    Use a different email
                </a>
            </div>

        </template>

    </div>
</template>

<script>
import { defineComponent } from 'vue';

/**
 * LoginApp - остров страницы /login (порт Login.vue донора в вёрстке
 * Spark-карточки).
 *
 * Контракт ajax:
 *   POST /auth/login  { email, redirect } -> { sent, token, time }
 *   POST /auth/verify { token, code, redirect } -> { token, redirect }
 * Куку ставит фронт (канон донора): auth_token/base_domain волной
 * window-переменных из volt (settings пишет baseURL, mount - остальное).
 */
export default defineComponent({

    name: 'LoginApp',

    props: {
        authToken : { type: String, default: '' },
        domain    : { type: String, default: '' },
    },

    data() {
        return {
            isLoading  : false,
            email      : '',
            loginToken : '',
            sentTo     : '',
            redirect   : new URLSearchParams( window.location.search ).get('redirect') || '',
            blocked    : false,
            code       : [ '', '', '', '', '', '' ],
            codeInputs : [],
            error      : '',
            time       : 0,
            timerId    : null,
        };
    },

    computed: {

        isCodeComplete() {
            return this.code.every( digit => digit !== '' );
        },

        fullCode() {
            return this.code.join('');
        },

        formattedTime() {

            const m = Math.floor( this.time / 60 );
            const s = this.time % 60;

            return `${ m }:${ s.toString().padStart( 2, '0' ) }`;
        },
    },

    methods: {

        isValidEmail( email ) {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( email );
        },

        startTimer( seconds ) {

            this.time = seconds;

            if( this.timerId )
                clearInterval( this.timerId );

            this.timerId = setInterval( () => {
                this.time -= 1;
                if( this.time <= 0 ) {
                    clearInterval( this.timerId );
                    this.timerId = null;
                }
            }, 1000 );
        },

        onCodeInput( index, event ) {

            const value = event.target.value;

            if( ! /^\d*$/.test( value ) ) {
                this.code[ index ] = '';
                return;
            }

            if( value && index < 5 ) {
                this.$nextTick( () => {
                    this.codeInputs[ index + 1 ]?.focus();
                } );
            }

            if( this.isCodeComplete )
                this.verifyCode();
        },

        onCodeKeydown( index, event ) {

            if( event.key === 'Backspace' && ! this.code[ index ] && index > 0 ) {
                this.$nextTick( () => {
                    this.codeInputs[ index - 1 ]?.focus();
                } );
            }

            if( event.key === 'ArrowLeft' && index > 0 ) {
                event.preventDefault();
                this.codeInputs[ index - 1 ]?.focus();
            }

            if( event.key === 'ArrowRight' && index < 5 ) {
                event.preventDefault();
                this.codeInputs[ index + 1 ]?.focus();
            }
        },

        onCodePaste( event ) {

            event.preventDefault();
            const pasted = event.clipboardData.getData('text').replace( /\D/g, '' ).slice( 0, 6 );

            if( ! pasted )
                return;

            pasted.split('').forEach( ( digit, i ) => {
                if( i < 6 )
                    this.code[ i ] = digit;
            } );

            const next = Math.min( pasted.length, 5 );
            this.$nextTick( () => {
                this.codeInputs[ next ]?.focus();
            } );

            if( pasted.length === 6 )
                this.$nextTick( () => this.verifyCode() );
        },

        clearCode() {
            this.code = [ '', '', '', '', '', '' ];
            this.$nextTick( () => {
                this.codeInputs[0]?.focus();
            } );
        },

        resendCode() {

            if( this.time > 0 )
                return;

            this.blocked = false;
            this.clearCode();
            this.submitEmail();
        },

        backToForm() {
            this.sentTo  = '';
            this.error   = '';
            this.blocked = false;
            this.clearCode();
        },

        requestMessage( err, fallback ) {
            return err?.response?.data?.data?.message
                || err?.response?.data?.message
                || fallback;
        },

        /**
         * Шаг 1: email -> письмо. token и time - себе, письмо - юзеру.
         */
        async submitEmail() {

            if( this.isLoading )
                return;

            const value = this.email.trim();

            if( ! this.isValidEmail( value ) ) {
                this.error = 'Enter a valid email address';
                return;
            }

            this.isLoading = true;
            this.error     = '';

            try {

                const response = await this.$http.post( '/auth/login', {
                    email    : value,
                    redirect : this.redirect,
                } );
                const { status, data } = response?.data || {};

                if( status !== 'success' )
                    throw new Error( data?.message || 'Failed to send login link' );

                this.loginToken = data.token || '';
                this.sentTo     = value;
                this.startTimer( data.time || 0 );

                this.$nextTick( () => {
                    this.codeInputs[0]?.focus();
                } );

            } catch( err ) {
                this.error = this.requestMessage( err, err.message || 'Failed to send login link. Please try again.' );
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Шаг 2: код -> витринный JWT -> кука фронтом -> redirect.
         */
        async verifyCode() {

            if( this.isLoading || ! this.isCodeComplete || this.blocked )
                return;

            this.isLoading = true;
            this.error     = '';

            try {

                const response = await this.$http.post( '/auth/verify', {
                    token    : this.loginToken,
                    code     : this.fullCode,
                    redirect : this.redirect,
                } );
                const { status, code, data } = response?.data || {};

                if( status !== 'success' ) {

                    // Попытки исчерпаны - блокируем форму до resend
                    // (magic-ссылка того же письма тоже мертва: общий токен)
                    if( code === 429 ) {
                        this.blocked = true;
                        this.error   = '';
                        return;
                    }

                    this.error = data?.message || 'Invalid code. Please try again.';
                    this.clearCode();
                    return;
                }

                if( data?.token ) {
                    const expires = new Date( Date.now() + 86400 * 1000 ).toUTCString();
                    document.cookie = `${ this.authToken }=${ data.token }; path=/; domain=.${ this.domain }; expires=${ expires }; secure; samesite=strict`;
                }

                window.location.href = data?.redirect || '/account';

            } catch( err ) {

                if( err?.response?.data?.code === 429 ) {
                    this.blocked = true;
                    this.error   = '';
                    return;
                }

                this.error = this.requestMessage( err, 'Invalid or expired code. Please try again.' );
                this.clearCode();
            } finally {
                this.isLoading = false;
            }
        },
    },
});
</script>

<style scoped>
</style>
